// CPC: 扫码下载二维码（零 npm 依赖；浏览器拉取 QR 图，失败则展示链接）
import { useMemo, useState } from 'react';

type CpcDownloadQrProps = {
	url: string;
	label?: string;
	size?: number;
	className?: string;
};

/** 解析为手机可访问的绝对 URL */
export function resolveDownloadUrl(url: string): string {
	if (typeof window === 'undefined') return url;
	try {
		return new URL(url, window.location.href).href;
	} catch {
		return url;
	}
}

function isLikelyUnreachableFromPhone(url: string): boolean {
	try {
		const host = new URL(url).hostname;
		return (
			host === 'localhost' ||
			host === '127.0.0.1' ||
			host === '0.0.0.0' ||
			host === '::1' ||
			host.endsWith('.local')
		);
	} catch {
		return false;
	}
}

export function CpcDownloadQr({
	url,
	label = '手机扫码下载',
	size = 132,
	className,
}: CpcDownloadQrProps) {
	const [failed, setFailed] = useState(false);
	const abs = useMemo(() => resolveDownloadUrl(url), [url]);
	const localUnreachable = useMemo(() => isLikelyUnreachableFromPhone(abs), [abs]);
	// 双源：主用 qrserver，失败换 quickchart（仅生成展示用图片，扫码内容仍是 abs）
	const sources = useMemo(
		() => [
			`https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&ecc=M&data=${encodeURIComponent(abs)}`,
			`https://quickchart.io/qr?size=${size}&margin=2&text=${encodeURIComponent(abs)}`,
		],
		[abs, size],
	);
	const [srcIdx, setSrcIdx] = useState(0);

	return (
		<div className={className ?? 'flex flex-col items-center gap-2'}>
			{failed ? (
				<div
					className="flex items-center justify-center rounded-lg border border-dashed border-white/20 bg-white/[0.04] p-3 text-center"
					style={{ width: size + 16, minHeight: size + 16 }}
				>
					<p className="break-all px-1 text-[10px] leading-snug text-[#94a3b8]">{abs}</p>
				</div>
			) : (
				<img
					src={sources[srcIdx]}
					alt={label}
					width={size}
					height={size}
					className="rounded-lg bg-white p-1.5 shadow-sm"
					loading="lazy"
					decoding="async"
					onError={() => {
						if (srcIdx + 1 < sources.length) setSrcIdx(srcIdx + 1);
						else setFailed(true);
					}}
				/>
			)}
			<p className="text-center text-[11px] font-medium text-[#94a3b8]">{label}</p>
			{localUnreachable ? (
				<p className="max-w-[11rem] text-center text-[10px] leading-snug text-[#fb923c]">
					当前是本机地址，手机扫不开。请用公网 IP/域名打开本页后再扫。
				</p>
			) : null}
		</div>
	);
}
